import useTranslation from 'next-translate/useTranslation';
import React, { useEffect, useState } from 'react';
import COUNTRY_NAMES from '@/lib/data/country-names.json';
import { useForm } from 'react-hook-form';
import { saveBuylead } from '@/lib/repository/crm/CRMRepository';
import { ProductItem } from '@/lib/types/api';
import { Input } from './Input';
import { Select } from './Select';
import { SelectCountry } from './SelectCountry';
import { InputTerms } from './InputTerms';
import {
  getGTMProductData,
  pushDataLayer,
} from '@/lib/utilities/analytics/gtm';
import { useRouter } from 'next-translate-routes-multi-domain';
import { bingUET } from '@/lib/utilities/analytics/bing';
import { SuccessCheck } from './Success/SuccessCheck';
import { hasPhonePrefix } from '@/lib/data-transformer/gindumacUtils';
import useUserMetadata from '@/lib/hooks/useUserData';
import { useUser } from '@auth0/nextjs-auth0/client';
import { LinkEditMetadata } from '../ui/Links/LinkEditMetadata';
import { useAccountManagerByLocale } from '@/lib/hooks/useAccountManager';

type FormInputs = {
  'account-manager_company': string;
  'account-manager_email': string;
  'account-manager_first-name': string;
  'account-manager_last-name': string;
  'account-manager_city': string;
  'account-manager_country': string;
  'account-account-manager_company': string;
  'account-manager_phone-number': string;
  'account-manager_phone-prefix': string;
  'account-manager_subject': string;
  'account-manager_message': string;
  'account-manager_terms': boolean;
};

function AccountManagerForm({ product }: { product: ProductItem }) {
  const { t } = useTranslation('products');
  const { locale } = useRouter();
  const { data: accountManager } = useAccountManagerByLocale();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitSuccessful, isSubmitting },
    reset,
  } = useForm<FormInputs>();

  async function onSubmit(data: FormInputs) {
    console.log('data', data);
    try {
      await saveBuylead({
        'platform-name': 'GINDUMAC Website',
        'platform-type': 'Product Detail Request',
        country: data['account-manager_country'],
        city: data['account-manager_city'],
        message: data['account-manager_message'],
        subject: data['account-manager_subject'],
        'phone-number': data['account-manager_phone-number'],
        'phone-prefix': data['account-manager_phone-prefix'],
        email: data['account-manager_email'],
        company: data['account-manager_company'],
        'first-name': data['account-manager_first-name'],
        'last-name': data['account-manager_last-name'],
        sku: product.sku,
      });
      tracking();
    } catch (error) {
      console.log('error', error);
    }
  }

  function tracking() {
    pushDataLayer('formSubmission', {
      formType: 'Account Manager Form',
      locale: locale,
    });
    pushDataLayer('begin_checkout', {
      formType: 'Account Manager Form',
      ecommerce: {
        value: product.price,
        currency: product.currency,
        items: [getGTMProductData(product)],
      },
    });
    bingUET('Contact Form QM');
  }

  const [countriesOptions, setCountriesOptions] = useState<
    { label: string; code: string; phoneCode: string; nativeLabel: string }[]
  >([]);

  useEffect(() => {
    const options = COUNTRY_NAMES.map(
      // translate
      (country) => [country[0], t(`${country[1]}`), country[2], country[3]]
    )
      .sort((a, b) => {
        const nameA = a[1].replace(/(\b\w)/gi, (m) => {
          return m.toUpperCase();
        });
        const nameB = b[1].replace(/(\b\w)/gi, (m) => {
          return m.toUpperCase();
        });
        return nameA.localeCompare(nameB);
      })
      .map((country) => ({
        code: country[0],
        label: country[1],
        nativeLabel: country[2],
        phoneCode: country[3],
      }));
    setCountriesOptions(options);
  }, [t]);

  const { metadata } = useUserMetadata();
  const { user } = useUser();
  const [hasPrefixInput, setHasPrefixInput] = useState(false);
  useEffect(() => {
    function handleReset() {
      const phone = metadata?.phone ?? metadata?.companyPhone;
      setHasPrefixInput(!hasPhonePrefix(phone));
      reset({
        'account-manager_first-name': metadata?.firstName,
        'account-manager_last-name': metadata?.lastName,
        'account-manager_company': metadata?.companyName,
        'account-manager_city': metadata?.city ?? metadata?.companyCity,
        'account-manager_email': user?.email ?? metadata?.companyEmail,
        'account-manager_phone-number': phone,
        'account-manager_country': COUNTRY_NAMES.find(
          (e) => e[1] === (metadata?.country ?? metadata?.companyCountry)
        )?.[0],
      });
    }
    // run handleReset after one sec
    const timer = setTimeout(() => handleReset(), 1000);
    return () => clearTimeout(timer);
  }, [metadata, reset, user?.email, setHasPrefixInput]);

  return (
    <div className='no-print w-full bg-gray-light p-8'>
      <div className='container min-[1400px]:max-w-[45%]'>
        <div className=''>
          <div className=''>
            <div className=''>
              <strong className='text-sm uppercase text-muted-500'>
                {t('products:DETAIL_ACCMANAGER_HEADER')}
              </strong>
              <p className='h3 pt-2 text-3xl font-light'>
                {accountManager?.name}
              </p>
              <div className=''>
                <div className='py-6 font-light text-muted-500'>
                  {accountManager?.name}{' '}
                  {t('products:QUALITY_MANAGER_DESCRIPTION')}
                </div>
                {isSubmitSuccessful ? (
                  <SuccessCheck />
                ) : (
                  <form
                    id='account-manager_form'
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    <p className='h4 mb-6 uppercase'>
                      {t('products:DETAIL_ACCMANAGER_CONTACT')}
                    </p>
                    <div className='mb-4'>
                      <LinkEditMetadata />
                    </div>
                    <div className='flex space-x-4 pb-4'>
                      <div className='w-1/2'>
                        <Input<FormInputs>
                          placeholder={t('FORM_PLACEHOLDER_COMPANY')}
                          name='account-manager_company'
                          validation={{
                            required: true,
                            maxLength: 80,
                          }}
                          register={register}
                          errors={errors}
                        />
                      </div>
                      <div className='w-1/2'>
                        <Input<FormInputs>
                          type='email'
                          placeholder={t('FORM_PLACEHOLDER_EMAIL')}
                          name='account-manager_email'
                          validation={{
                            required: true,
                            maxLength: 80,
                          }}
                          register={register}
                          errors={errors}
                        />
                      </div>
                    </div>
                    <div className='flex space-x-4 pb-4'>
                      <div className='w-1/2'>
                        <Input<FormInputs>
                          placeholder={t('FORM_PLACEHOLDER_FIRSTNAME')}
                          name='account-manager_first-name'
                          validation={{
                            required: true,
                            maxLength: 80,
                          }}
                          register={register}
                          errors={errors}
                        />
                      </div>
                      <div className='w-1/2'>
                        <Input<FormInputs>
                          placeholder={t('FORM_PLACEHOLDER_LASTNAME')}
                          name='account-manager_last-name'
                          validation={{
                            required: true,
                            maxLength: 80,
                          }}
                          register={register}
                          errors={errors}
                        />
                      </div>
                    </div>
                    <div className='flex space-x-4 pb-4'>
                      <div className='w-1/2'>
                        <Input<FormInputs>
                          placeholder={t('FORM_PLACEHOLDER_CITY').toUpperCase()}
                          name='account-manager_city'
                          validation={{
                            required: true,
                            maxLength: 80,
                          }}
                          register={register}
                          errors={errors}
                        />
                      </div>
                      {/* A select input with a floating label */}
                      <div className='w-1/2'>
                        <Select<FormInputs>
                          name='account-manager_country'
                          options={countriesOptions.map((country) => ({
                            value: country.code,
                            label: country.label.toUpperCase(),
                            key: country.code + country.phoneCode,
                          }))}
                          validation={{
                            required: true,
                          }}
                          register={register}
                          errors={errors}
                          label={t(
                            'FORM_PLACEHOLDER_COUNTRY_NAME'
                          ).toUpperCase()}
                          value={watch('account-manager_country')}
                        />
                      </div>
                    </div>
                    <div className='flex space-x-4 pb-4'>
                      {hasPrefixInput && (
                        <div className='relative w-1/2'>
                          <SelectCountry<FormInputs>
                            type='phoneCode'
                            name='account-manager_phone-prefix'
                            value={watch('account-manager_phone-prefix')}
                            errors={errors}
                            register={register}
                            validation={{
                              required: true,
                            }}
                          />
                        </div>
                      )}

                      <div className={hasPrefixInput ? 'w-1/2' : 'w-full'}>
                        <Input<FormInputs>
                          placeholder={t('FORM_PLACEHOLDER_PHONE')}
                          name='account-manager_phone-number'
                          validation={{
                            required: true,
                            maxLength: 80,
                          }}
                          register={register}
                          errors={errors}
                        />
                      </div>
                    </div>
                    <div className='flex space-x-4 pb-4'>
                      <div className='w-full'>
                        <Input<FormInputs>
                          placeholder={t(
                            'FORM_PLACEHOLDER_SUBJECT'
                          ).toUpperCase()}
                          name='account-manager_subject'
                          validation={{
                            required: true,
                            maxLength: 100,
                          }}
                          register={register}
                          errors={errors}
                        />
                      </div>
                    </div>
                    <div className='flex space-x-4 pb-4'>
                      <Input<FormInputs>
                        type='textarea'
                        placeholder={t(
                          'FORM_PLACEHOLDER_MESSAGE'
                        ).toUpperCase()}
                        name='account-manager_message'
                        validation={{
                          required: true,
                          maxLength: 2500,
                        }}
                        register={register}
                        errors={errors}
                      />
                    </div>
                    <div className='mb-4'>
                      <div className='input-field gd-input-field col l9 m9 s10'>
                        <InputTerms<FormInputs>
                          name='account-manager_terms'
                          register={register}
                          errors={errors}
                        />
                      </div>
                    </div>
                    <div className=''>
                      <div className='flex justify-end'>
                        <button
                          className='rounded bg-brand-500 px-24 py-4 text-sm uppercase tracking-wider text-white'
                          type='submit'
                          data-testid='account-manager_form_submit'
                          disabled={isSubmitting}
                        >
                          {t('FORM_BTN_SEND')}
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountManagerForm;
